import { ACCESS_TOKEN, API_BASE_URL_DOCUMENT } from './config.js';
import { message, notification } from 'antd';
import handleErrors from './handleErrors';
import _, { isArray } from 'lodash';
import React from 'react';
import { encrypt } from './crypto';
import getEnvValue from './getEnvValue';

const SESSION_TIMEOUT_MESSAGE = 'Session expirée. Veuillez vous reconnecter.';

type JwtPayload = {
  exp?: number;
};

const decodeBase64 = (value: string) => {
  if (typeof atob === 'function') {
    return atob(value);
  }

  if (
    typeof globalThis !== 'undefined' &&
    typeof (globalThis as any).atob === 'function'
  ) {
    return (globalThis as any).atob(value);
  }

  return '';
};

const decodeJwtPayload = (token: string): JwtPayload | null => {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(
      Math.ceil(normalized.length / 4) * 4,
      '='
    );
    const decoded = decodeBase64(padded);
    return JSON.parse(decoded);
  } catch (err) {
    return null;
  }
};

const isTokenExpired = (token: string | null) => {
  if (!token || token === 'undefined') {
    return true;
  }

  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return false;

  const nowInSeconds = Math.floor(Date.now() / 1000);
  // add a 15 second buffer to avoid racing the backend
  return payload.exp <= nowInSeconds + 15;
};

const clearSessionAndRedirect = () => {
  localStorage.removeItem('changepassword');
  localStorage.removeItem('token');
  localStorage.clear();

  if (window.location.pathname !== '/login') {
    window.history.replaceState(null, '', '/login');
    window.location.reload();
  }
};

const handleSessionTimeout = (
  message = SESSION_TIMEOUT_MESSAGE,
  description = SESSION_TIMEOUT_MESSAGE
) => {
  notification.error({ message, description });
  clearSessionAndRedirect();
};

type Method = 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH';

type Options = {
  url: string;
  method: Method;
  body?: any;
};

export function request<Data = any>(options: Options): Promise<Data>;
// eslint-disable-next-line no-redeclare
export function request<Data = any>(
  url: string,
  options: Omit<Options, 'url'>
): Promise<Data>;
// eslint-disable-next-line no-redeclare
export function request<Data = any>(
  optionsOrUrl: Options | string,
  maybeOptions?: Omit<Options, 'url'>
): Promise<Data> {
  let url = typeof optionsOrUrl === 'string' ? optionsOrUrl : optionsOrUrl.url;
  const options =
    typeof optionsOrUrl === 'string' ? maybeOptions : optionsOrUrl;

  const { body, method, ...otherOptions } = options as Options;

  const headers = new Headers({
    'Content-Type': 'application/json',
  });

  if (
    // process.env.NODE_ENV === 'production' ||
    getEnvValue('REACT_APP_ENV_MODE') === 'PROD'
  )
    url =
      url.slice(0, url.indexOf('/api') + 5) +
      encrypt(url.slice(url.indexOf('/api') + 5, url.length));

  const accessToken = localStorage.getItem(ACCESS_TOKEN);

  if (accessToken !== 'undefined' && accessToken != null) {
    if (isTokenExpired(accessToken)) {
      handleSessionTimeout();
      return Promise.reject({
        code: '401',
        status: 401,
        message: SESSION_TIMEOUT_MESSAGE,
      } as any);
    }
    headers.append('token', `Bearer ${accessToken}`);
  } else {
    headers.append('token', 'Bearer');
  }
  headers.append('Dev-Stage', 'true');

  const defaults = { headers, mode: 'cors' as const };

  const requestOptions = Object.assign({}, defaults, {
    method,
    body:
      body !== undefined && typeof body !== 'string'
        ? JSON.stringify(body)
        : body,
    ...otherOptions,
  });

  return fetch(url, requestOptions)
    .then(handleErrors)
    .then((response) => {
      return response?.json()?.then((json) => {
        if (json.code !== undefined) {
          if (json.code === '417') {
            try {
              let errormsg: any;
              if (json.description) {
                let listError = JSON.parse(json.description);
                if (isArray(listError)) {
                  errormsg = React.createElement(
                    'table',
                    { className: 'styled-table' },
                    null,
                    React.createElement(
                      'thead',

                      null,
                      React.createElement(
                        'tr',
                        { className: 'active-row' },
                        React.createElement(
                          'th',

                          null,
                          'Code'
                        ),
                        React.createElement(
                          'th',

                          null,
                          'Message'
                        )
                      )
                    ),

                    listError.map((error: any) => {
                      return React.createElement(
                        'tr',
                        { className: 'active-row' },
                        React.createElement(
                          'td',
                          { style: { fontWeight: '700' } },
                          error.ccode
                        ),
                        React.createElement(
                          'td',

                          null,
                          error.message
                        )
                      );
                    })
                  );
                } else {
                  errormsg = listError;
                }
              }
              notification.error({
                description: errormsg,
                message: React.createElement(
                  'span',
                  { style: { fontWeight: '700', color: '#660000' } },
                  json.message
                ),
                className: 'error417',
                style: {
                  width: 400,
                  padding: '9px 12px 1px 12px',
                  marginLeft: 0,
                },
              });
              return Promise.reject(json);
            } catch (Exception) {
              notification.error({
                description: json.error_description || json.description,
                message: json.message,
              });
            }
          }
          if (json.code === '500') {
            notification.error({
              description: json.error_description || json.description,
              message: json.message,
            });
          }
          if (json.code === '401') {
            handleSessionTimeout(
              json.message,
              json.error_description || json.description || 'Session Expirée'
            );
            return Promise.reject(json);
          }

          if (json.code === '403') {
            notification.error({
              description: json.error_description || json.description,
              message: json.message,
            });
            // window.history.replaceState(null, '', '/403');
            // window.location.reload();
          }

          if (json.code === '404') {
            notification.error({
              description: json.error_description || json.description,
              message: json.message || 'Resource not found',
            });
          }

          if (json.code === '400') {
            notification.error({
              description: json.error_description || json.description,
              message: json.message || 'Bad Request',
            });
          }
        }

        if (!response.ok) {
          try {
            if (json.status === '500') {
              message.error(`${json.status}: ${json.message}`);
            }
          } catch (err) {
            let service = url.substring(
              url.indexOf('/', 10) + 1,
              url.indexOf('/api/', 1)
            );
            message.warning({
              content: `${service} is not available`,
              style: {
                fontSize: 18,
              },
            });
          }
          return Promise.reject(json);
        }

        return json;
      });
    });
}

request.get = <Data = any>(url: string) => {
  return request<Data>(url, { method: 'GET' });
};
request.delete = <Data = any>(url: string, body?: any) => {
  return request<Data>(url, { method: 'DELETE', body });
};
request.post = <Data = any>(url: string, body?: any) => {
  return request<Data>(url, { method: 'POST', body });
};
request.patch = <Data = any>(url: string, body?: any) => {
  return request<Data>(url, { method: 'PATCH', body });
};
request.put = <Data = any>(url: string, body?: any) => {
  return request<Data>(url, { method: 'PUT', body });
};
export async function UploadDocumentFile(file: any, objectReference: string) {
  const formData = new FormData();
  formData.append('file', file);
  const accessToken = localStorage.getItem(ACCESS_TOKEN);

  objectReference = objectReference + '_' + Math.round(_.random(1000, 9999));

  try {
    const res = await fetch(
      `${API_BASE_URL_DOCUMENT}/document/v1/file?objectReference=`,
      {
        method: 'POST',
        body: formData,
        headers: {
          token: `Bearer ${accessToken}`,
        },
      }
    );

    if (res.status === 200) {
      const result = await res.json();
      console.log('+++', result); 
      return result; 
    } else {
      console.log('___test', res);
      try {
        const _res = await res.json();
        console.log('+++', _res); 
        return {
          code: _res.status,
          message: _res.message,
          // description: _res.message,
        };
      } catch (error) {
        message.error('Error');
        return { code: '500', message: 'Error parsing response' };
      }
    }
  } catch (error) {
    message.error('Error');
    return { code: '500', message: 'Request failed' };
  }
}

// export async function UploadDocumentFile(file: any, objectReference: string) {
//   const formData = new FormData();

//   formData.append('file', file);
//   const accessToken = localStorage.getItem(ACCESS_TOKEN);

//   objectReference = objectReference + '_' + Math.round(_.random(1000, 9999));

//   const results = await fetch(
//     `${API_BASE_URL_DOCUMENT}/upload?objectReference=`,
//     {
//       method: 'POST',
//       body: formData,
//       headers: {
//         Authorization: `Bearer ${accessToken}`,
//       },
//     }
//   ).then((res) => {

//     if (res.status === 200) {

//       return res.json();
//       // return {
//       //   code: '200',
//       //   description: res.json(),
//       //   message: 'success',
//       // };
//     } else {
//       console.log('___test', res);
//       try {
//         res.json().then(_res=>{
//           console.log('+++',_res)
//           return{
//             code: _res.status,
//             message: _res.message,
//             description: _res.message
//           }
//         })
//       } catch(error) {
//         message.error('Error');
//       }
//       // return {
//       //   code: '500',
//       //   // description: 'error',
//       //   message: res.statusText,
//       // };
//     }
//   });

//   console.log('results', results);

//   return results;
// }
